import { collection, query, where, orderBy, limit, getDocs, Timestamp, getDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { getWordsData } from './utils';
import { pickExampleSentenceIdForWord } from './words';

const RECENT_UNLOCKS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SESSION_LIMIT = 50;

const getCutoff = () => Timestamp.fromDate(new Date(Date.now() - RECENT_UNLOCKS_WINDOW_MS));

/**
 * Fetches recently unlocked sentences — user_sentences with learned_at within the past 7 days,
 * not skipped, ordered by most recent first, capped at 50.
 */
export const fetchRecentUnlocksSentencesItems = async (userId) => {
  const cutoff = getCutoff();
  const colRef = collection(firestore, 'users', userId, 'user_sentences');
  const q = query(
    colRef,
    where('learned_at', '>=', cutoff),
    where('skipped', '==', false),
    orderBy('learned_at', 'desc'),
    limit(SESSION_LIMIT)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) return [];

  const sentencePromises = snapshot.docs.map(async (docSnap) => {
    const userSentenceData = { id: docSnap.id, ...docSnap.data() };
    const sentenceRef = userSentenceData.sentence_ref;
    if (!sentenceRef) return null;

    const sentenceDoc = await getDoc(sentenceRef);
    if (!sentenceDoc.exists()) return null;

    const merged = { ...userSentenceData, ...sentenceDoc.data(), id: docSnap.id };
    merged.words = await getWordsData(merged.words || []);
    return merged;
  });

  const results = await Promise.all(sentencePromises);
  return results.filter(Boolean);
};

/**
 * Fetches recently unlocked words — user_words with learned_at within the past 7 days,
 * not skipped, ordered by most recent first, capped at 50.
 */
export const fetchRecentUnlocksWordsItems = async (userId, learnedSentenceIds = null) => {
  const cutoff = getCutoff();
  const colRef = collection(firestore, 'users', userId, 'user_words');
  const q = query(
    colRef,
    where('learned_at', '>=', cutoff),
    where('skipped', '==', false),
    orderBy('learned_at', 'desc'),
    limit(SESSION_LIMIT)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) return [];

  const wordPromises = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    word_id: docSnap.data().word_ref,
    ...docSnap.data(),
  }));

  const wordsData = await getWordsData(wordPromises);
  return Promise.all(
    wordsData.map(async (word) => {
      try {
        const id = await pickExampleSentenceIdForWord(word.id, learnedSentenceIds);
        return { ...word, exampleSentenceId: id };
      } catch {
        return { ...word, exampleSentenceId: null };
      }
    })
  );
};
